/**
 * Origin of the ora web app — the "one hop away" monitor the pinned pane
 * points at.
 *
 * CONFIGURED, NEVER DISCOVERED. `VITE_ORA_WEB_URL` is the only source (under
 * `ctr dev --instance`, vite.config folds `WF_PRODUCT_UI_PORT` into the same
 * global). There is no candidate list and no derivation from the API port:
 * `localhost:5173` is Vite's *container* port, and an instance stack publishes
 * it on the host somewhere else entirely (21315 for emuw-merged), so any guess
 * is a coin flip that opens a dead window.
 *
 * This replaced a probe that fetched each of five candidate origins to see
 * which answered. That probe could never succeed: it paired `mode: "no-cors"`
 * with `redirect: "manual"`, a combination the Fetch spec forbids, so every
 * candidate threw a TypeError at the Request constructor before a packet left
 * the process. It logged five console errors on every pane load and fell
 * through to the same static fallback every time — a discovery mechanism that
 * only ever discovered its own default.
 *
 * Unset is a CONFIGURATION ERROR and says so. Silently falling back to a
 * plausible port is how you get a monitor window that opens on nothing and a
 * person who cannot tell whether the queue is empty or the app is missing.
 *
 * Opening goes through Office `openBrowserWindow` so the hop lands in the
 * OS default browser (Edge, if that is what Windows has registered), not
 * an Office-owned Chromium dialog. `displayDialogAsync` is the auth
 * surface; it is the wrong tool for "go look at ora".
 */

/**
 * `vite.config.ts` injects `__OUTLOOK_ADDIN_ORA_WEB_URL__` at build time from
 * `VITE_ORA_WEB_URL` (falling back to `WF_PRODUCT_UI_PORT`).
 *
 * READ AS A BARE IDENTIFIER, never through a computed `globalThis[...]` lookup.
 * Vite's `define` is an IDENTIFIER substitution: it rewrites that token, not a
 * member access keyed by the same text as a string. A `globalThis[name]` lookup
 * therefore survives the build verbatim and resolves to `undefined` at runtime,
 * so the configured origin could never arrive and every hop threw "not
 * configured" no matter what sat in `.env` or App Config. That was this
 * module's actual bug: the explicit-config branch had never once executed, and
 * it went unnoticed only because the fallback chain it replaced happened to
 * yield `http://localhost:5173`, which is right locally and wrong everywhere
 * else.
 *
 * `typeof X !== "undefined"` keeps this safe under jest, where the global is
 * genuinely absent: an undeclared identifier is a legal `typeof` operand, and
 * the suite's `globalThis` assignment still resolves through the scope chain.
 * `auth.ts` reads `__OUTLOOK_ADDIN_AAD_CONFIG__` exactly this way and has
 * always worked — this module was the only one that strayed.
 */
declare const __OUTLOOK_ADDIN_ORA_WEB_URL__: string | undefined;

/** The configured ora origin, or `""` when nothing was configured. */
export function oraWebOrigin(): string {
  const raw =
    typeof __OUTLOOK_ADDIN_ORA_WEB_URL__ !== "undefined"
      ? __OUTLOOK_ADDIN_ORA_WEB_URL__
      : undefined;
  return typeof raw === "string" && raw.trim() ? raw.replace(/\/$/, "") : "";
}

/** True when an ora origin was supplied at build time. */
export function isOraWebConfigured(): boolean {
  return oraWebOrigin() !== "";
}

/** Join a path onto the configured ora origin (no trailing slash on origin). */
export function oraWebPath(path: string, origin?: string): string {
  const base = origin ?? oraWebOrigin();
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

export function oraMonitorUrl(origin?: string): string {
  return oraWebPath("/workflows/monitor", origin);
}

export function oraMailboxesUrl(origin?: string): string {
  return oraWebPath("/admin/workflows/monitored-mailboxes", origin);
}

const ALLOWED_HOSTS = /^(localhost|127\.0\.0\.1)$/i;
const ALLOWED_PUBLIC = /(^|\.)myora\.ai$/i;

export function isAllowedOraUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    if (u.protocol !== "http:" && u.protocol !== "https:") return false;
    return ALLOWED_HOSTS.test(u.hostname) || ALLOWED_PUBLIC.test(u.hostname);
  } catch {
    return false;
  }
}

export function oraMonitorDialogUrl(target: string): string {
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://localhost:3100";
  return `${origin}/oramonitor.html?to=${encodeURIComponent(target)}`;
}

/**
 * Open an ora path in the OS default browser.
 *
 * `Office.context.ui.openBrowserWindow` asks Windows for the registered
 * http/https handler. Throws when the origin is unconfigured.
 */
export async function openOraWebPath(path: string): Promise<void> {
  const origin = oraWebOrigin();
  if (!origin) {
    throw new Error(
      "ora web origin is not configured. Locally: set VITE_ORA_WEB_URL in " +
        "ui/apps/outlook-addin/.env.local (or run under `ctr dev --instance`, " +
        "which supplies WF_PRODUCT_UI_PORT) and restart vite. In a deployed " +
        "environment: add VITE_ORA_WEB_URL to that env's App Config under the " +
        "OUTLOOK_ADDIN label, then rebuild and redeploy the add-in.",
    );
  }
  const target = oraWebPath(path, origin);

  const ui = (
    typeof Office !== "undefined" ? Office?.context?.ui : undefined
  ) as
    | {
        openBrowserWindow?: (url: string) => void;
      }
    | undefined;

  if (typeof ui?.openBrowserWindow === "function") {
    ui.openBrowserWindow(target);
    return;
  }

  window.open(target, "_blank", "noopener,noreferrer");
}

export async function openOraMonitor(): Promise<void> {
  return openOraWebPath("/workflows/monitor");
}

export async function openOraMailboxes(): Promise<void> {
  return openOraWebPath("/admin/workflows/monitored-mailboxes");
}
